/* First-party site events tracker. Dependency-free, fails silent, no PII.
   Config arrives via <script type="application/json" data-fme-config>
   (rendered by snippets/site-events.liquid). Batches events and ships them
   to the Cloud Run ingest with sendBeacon; consent-gated via
   Shopify.customerPrivacy (absent API = track, US default).
   Event taxonomy + schema: source.site_events in BigQuery.

   SDK 2.0 (2026-08-20). What changed and why, since every line of it was
   asked for by the analysis side and half of it is non-obvious:

   - Geometry is measured AT EMIT TIME (A2). The context envelope is built
     once at load, so anything read from it describes the page as it was at
     boot. A click's element box, the scroll position and the document height
     are now measured when the click happens, which is what makes "was this on
     screen, and how far past it did they scroll" answerable from BigQuery
     without re-rendering an archived copy of the page.
   - element_view fires the MOMENT an element crosses the threshold (B1), not
     at exit. page_exit is not reliable - it is the reason page_open exists -
     and holding a session's whole visibility record until exit means one lost
     beacon loses the lot. Cumulative visible_ms still ships at exit, as
     enrichment: the durable fact and the perishable detail travel separately.
   - The scroll trace flushes incrementally (B3). Same reasoning, higher
     stakes: it is the one input that cannot be reconstructed afterwards.
   - An implausible viewport is FLAGGED, not dropped (C1). The viewport is
     per-pageview, so suppressing the event would throw away real interactions
     from that pageview; the flag lets analysis exclude it deliberately.
   - mode:'minimal' (the shaker PDP, slv2 arm C) records opens, scroll depth,
     exits and add-to-cart only: enough for conversion rate and AOV, with no
     click, section or element tracking on a page nobody instrumented.

   SDK 2.2 (2026-08-21), for the dashboard's Event Tester replay: audit mode
   (?fm_audit=<code>, per-tab, echoed on every event as props.audit_code,
   ~1s flush cadence for the queue AND the scroll-trace buffer) and a
   periodic 7s customer backstop flush replacing the old one-shot 10s timer.

   SDK 2.3 (same day), the single-device guardrail: audit mode expires 10
   minutes after WINDOW ACTIVATION (_fm_audit_at; a different code re-stamps,
   the same code never does), reverting to the customer cadence in place -
   no reload - and audit_state on/off events announce the exact transition
   moments so the dashboard notifies instead of inferring.
   Details at the AUDIT assignment and in boot().

   SDK 2.4 (same day), replay fidelity: rails named with data-carousel emit a
   state_change per SETTLED slide change (the cumulative carousel summary
   cannot be replayed and swipes produce no clicks), the rail engine learns
   sizer-wrapped slides (ReelUp) and late-hydrating rails (MutationObserver),
   and clicks stamp the moment's own viewport height (vh, via vpH()) and
   doc-space y (click_y) - ctx.viewport_h is boot-time and the iOS URL-bar
   collapse moves the real height by >100px within one pageview.

   SDK 2.5 (2026-08-22), pixel_event forwarding: the pixel-debug shim hands
   every captured fbq() track call to the fm:event bridge as a first-party
   `pixel_event` row (all traffic - a permanent record of what the browser
   pixel told Meta). boot() now announces the bridge with window.__fmSdkUp +
   an 'fm:sdk-ready' event so the shim, which parses in <head> long before
   this deferred file, knows when queued calls can drain. No behavior change
   for any other event type; the volume series gains a few pixel_event rows
   per tracked pageview, which is why this is a versioned release.

   SDK 2.6 (the header paragraph 2.6 shipped without): the Safari-continuation
   unbucketed leak. Gate hop URLs carry fm_t/fm_s with stale ones stripped, a
   cookie-less arrival carrying a current-test fm_t or matching a configured
   destination's URL shape is ADOPTED deterministically rather than re-rolled,
   and the replica runs a cookie-only gate-lite. Android physical-pixel
   viewports are corrected and flagged, device_class comes off the UA, and dpr
   is recorded.

   SDK 2.7: element_view gains an authored opt-out. data-track-view="off"
   suppresses a node's view row while leaving its data-track click id intact,
   so a page can name every interactive element for CLICK naming without
   emitting a view row per element. The check lives in add(), so it covers
   both sources (tagged nodes and CLICK_MAP matches) and an opted-out node
   consumes no ELEMENT_LIMIT slot. ELEMENT_LIMIT stays 80, but truncation is
   no longer silent: window.__fmWatch {n, limit, dropped}, a console.warn, and
   page_exit.els_dropped when non-zero. No page that existed before 2.7 is
   affected - the attribute appears nowhere in the repo before this release,
   so the predicate is false for every element on v4b, v2, v2b and the theme,
   and their element_view sets are byte-identical across the boundary. From
   2.7 onward, never read a missing element_view on a page that uses the
   opt-out as "nobody saw it" without checking that page's observed roster.

   SDK 2.8: page_path and attribution/click-id values are bounded before they
   enter the client batch. This preserves normal values while preventing an
   oversized or adversarial query string from inflating every event payload.

   SDK 2.9: swatch clicks carry both their tier and slot coordinates without
   changing the cross-arm swatch:* track name. Named carousel state changes
   carry auto:true when a page timer moved the rail, and carousel exit
   summaries split those movements into changes_auto while retaining total
   changes and unsplit slide_ms for replay and dwell analysis.

   SDK 2.11: opt-in controlled rails report their real selected index on
   mobile and desktop; automatic advances remain distinct from interaction.
   Opt-in layout snapshots retain section geometry after dynamic reflow.
   Existing pages without these attributes keep the previous behavior.

   SDK 2.10: arrivals marked utm_term=exclude are held outside the active A/B
   test by the gate. While the matching test-scoped _fm_excl cookie remains,
   page_view carries props.excluded:true so the held-out population stays
   visible after the marker URL is gone.
*/
(function () {
  'use strict';

  var cfgEl = document.querySelector('[data-fme-config]');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || !cfg.endpoint) return;
  /* The arm-A/B shaker PDP renders this tracker (mode:'minimal') AND the gate
     that redirects away from it. The tracker is deferred, so it only boots
     when the redirect is slow enough for DCL to fire first - and when it does,
     boot() claims sessionStorage._fm_pv, which makes the LANDER's page_open
     report entry:false. That is the one flag the three-arm comparison cannot
     afford to be wrong on exactly the rows it exists to compare. The gate sets
     this sentinel immediately before location.replace. */
  if (window.__fmRedirecting) return;
  var ENDPOINT = String(cfg.endpoint).replace(/\/+$/, '') + '/e';
  var MINIMAL = cfg.mode === 'minimal';

  /* ---------- tiny utils ---------- */

  function uuid() {
    try {
      var b = new Uint8Array(16);
      crypto.getRandomValues(b);
      b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
      var h = '', i;
      for (i = 0; i < 16; i++) h += (b[i] + 256).toString(16).slice(1);
      return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
    } catch (e) {
      return 'x' + Math.random().toString(16).slice(2) + Date.now().toString(16);
    }
  }
  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, maxAge) {
    try { document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + maxAge + '; SameSite=Lax; Secure'; } catch (e) {}
  }
  function throttle(fn, ms) {
    var last = 0, timer = null;
    return function () {
      var now = Date.now();
      if (now - last >= ms) { last = now; fn(); }
      else if (!timer) { timer = setTimeout(function () { timer = null; last = Date.now(); fn(); }, ms - (now - last)); }
    };
  }
  function round(n) { return Math.round(n) || 0; }
  function capped(value, max) {
    if (value == null) return null;
    return String(value).slice(0, max);
  }
  function scrollY() { return round(window.scrollY || document.documentElement.scrollTop || 0); }
  function docH() {
    var d = document.documentElement, b = document.body;
    return round(Math.max(d ? d.scrollHeight : 0, b ? b.scrollHeight : 0));
  }
  /* ONE viewport-height reference for every per-moment measurement (sdk 2.4):
     scroll-trace points and click stamps share this, visualViewport preferred
     because innerHeight lags the iOS URL-bar collapse and ignores pinch zoom.
     ctx.viewport_h deliberately stays a boot-time value - it is the
     envelope's device descriptor, not a measurement; per-moment truth rides
     on the events (props.vh, trace point vh). */
  function vpH() {
    var v = window.visualViewport;
    return round((v && v.height) || window.innerHeight || 0);
  }

  /* ---------- geometry (A2) ---------- */

  /* A fixed or sticky element has no meaningful document position: the sticky
     bar's clicks span 0.1% to 77.4% of document height purely because the bar
     follows the viewport. Flag it and analysis excludes it correctly instead
     of doing nonsense arithmetic on it. Walking to the root costs a handful of
     getComputedStyle calls on a rare event, so it is not cached. */
  function isFixed(el) {
    try {
      var node = el;
      for (var i = 0; node && node.nodeType === 1 && i < 12; i++) {
        var pos = getComputedStyle(node).position;
        if (pos === 'fixed' || pos === 'sticky') return true;
        node = node.parentElement;
      }
    } catch (e) {}
    return false;
  }
  /* Document-space box + the scroll state at this instant. Fixed elements
     keep VIEWPORT coordinates, which is the only thing their box can mean. */
  function geo(el, into) {
    var out = into || {};
    try {
      var sy = scrollY();
      out.scroll_y = sy;
      out.doc_h = docH();
      if (el && el.getBoundingClientRect) {
        var r = el.getBoundingClientRect();
        var fixed = isFixed(el);
        out.el = {
          top: round(fixed ? r.top : r.top + sy),
          left: round(fixed ? r.left : r.left + (window.scrollX || 0)),
          w: round(r.width), h: round(r.height),
          fixed: fixed
        };
      }
    } catch (e) {}
    return out;
  }

  /* ---------- identity ---------- */

  function anonId() {
    try {
      var id = getCookie('_fm_aid') || localStorage.getItem('_fm_aid');
      if (!id) id = uuid();
      setCookie('_fm_aid', id, 34560000);
      localStorage.setItem('_fm_aid', id);
      return id;
    } catch (e) { return null; }
  }
  var SESSION_GAP = 1800000; /* 30 min */
  function sessionId(touch) {
    try {
      var now = Date.now();
      var s = JSON.parse(localStorage.getItem('_fm_s') || 'null');
      if (!s || !s.id || now - s.t > SESSION_GAP) s = { id: uuid(), t: now };
      if (touch) { s.t = now; localStorage.setItem('_fm_s', JSON.stringify(s)); }
      return s.id;
    } catch (e) { return 'ns'; }
  }
  var PAGEVIEW_ID = uuid(); /* one boot of the tracker; groups scroll trace chunks */

  /* ---------- context ---------- */

  var qs;
  try { qs = new URLSearchParams(location.search); } catch (e) { qs = { get: function () { return null; } }; }

  if (qs.get('fm_internal') === '1') {
    try { localStorage.setItem('_fm_int', '1'); } catch (e) {}
    setCookie('_fm_int', '1', 31536000);
  } else if (qs.get('fm_internal') === '0') {
    /* the flag lasts a year, so there has to be a way back out of it - a
       phone used for QA is also somebody's real phone */
    try { localStorage.removeItem('_fm_int'); } catch (e) {}
    setCookie('_fm_int', '', 0);
  }
  var isInternal = false;
  try { isInternal = localStorage.getItem('_fm_int') === '1' || getCookie('_fm_int') === '1' || !!(window.Shopify && window.Shopify.designMode); } catch (e) {}

  /* sdk 2.2: audit mode, for the dashboard's Event Tester replay. The tester
     mints a code and opens the page with ?fm_audit=<code>; while it is set,
     every event echoes it (props.audit_code) and the flush cadence drops to
     ~1s (see boot()) so the replay watches the session near-live.
     sessionStorage on purpose: per-TAB, so it survives the gate's arm
     redirect (location.replace stays in the tab) and same-tab navigations,
     and two tabs never share a code; it dies with the tab, or early via
     fm_audit=0. Deliberately independent of fm_internal - ordinary internal
     traffic keeps the customer cadence.
     One reach limit, by ingest schema not by choice: scroll_trace events
     route to source.site_scroll_traces, whose columns are fixed, so the code
     is stamped but dropped there - trace rows join the audited session on
     pageview_id, which every site_events row carries as props.pv. */
  /* sdk 2.3 guardrail: the code dies 10 minutes after WINDOW ACTIVATION
     (_fm_audit_at), not 10 minutes after last use - a tester's phone left
     on a table must stop streaming like a tester's phone. A DIFFERENT code
     re-stamps (the dashboard regenerating opens a new window); the SAME code
     never re-stamps, so bouncing between pages cannot keep a window alive.
     The head beacon runs first on every page and shares the stamping rules;
     it leaves _fm_audit_new (activation to announce) and _fm_audit_off (a
     code it cleared on fm_audit=0) for THIS file to turn into audit_state
     events, because the beacon's one job is page_open. Expired keys are the
     tracker's to announce and delete; the beacon just stops echoing. */
  var AUDIT_TTL = 600000; /* 10 min */
  var qAudit = qs.get('fm_audit');
  var AUDIT = null, AUDIT_AT = 0, auditPending = [];
  try {
    var prevAudit = sessionStorage.getItem('_fm_audit');
    var offMark = sessionStorage.getItem('_fm_audit_off');
    if (offMark) {
      sessionStorage.removeItem('_fm_audit_off');
      auditPending.push({ code: offMark, state: 'off', reason: 'cleared' });
    }
    if (qAudit === '0') {
      if (prevAudit) auditPending.push({ code: prevAudit, state: 'off', reason: 'cleared' });
      sessionStorage.removeItem('_fm_audit');
      sessionStorage.removeItem('_fm_audit_at');
    } else {
      var auditCode = qAudit ? String(qAudit).slice(0, 64) : prevAudit;
      if (auditCode) {
        if (auditCode !== prevAudit || !sessionStorage.getItem('_fm_audit_at')) {
          sessionStorage.setItem('_fm_audit', auditCode);
          sessionStorage.setItem('_fm_audit_at', String(Date.now()));
          sessionStorage.setItem('_fm_audit_new', '1');
        }
        if (sessionStorage.getItem('_fm_audit_new') === '1') {
          sessionStorage.removeItem('_fm_audit_new');
          auditPending.push({ code: auditCode, state: 'on' });
        }
        AUDIT_AT = parseInt(sessionStorage.getItem('_fm_audit_at'), 10) || 0;
        if (Date.now() - AUDIT_AT >= AUDIT_TTL) {
          auditPending.push({ code: auditCode, state: 'off', reason: 'expired' });
          sessionStorage.removeItem('_fm_audit');
          sessionStorage.removeItem('_fm_audit_at');
          AUDIT_AT = 0;
        } else AUDIT = auditCode;
      }
    }
  } catch (e) {
    AUDIT = qAudit && qAudit !== '0' ? String(qAudit).slice(0, 64) : null;
    AUDIT_AT = AUDIT ? Date.now() : 0;
  }

  var MOB = /\bMobi/i.test(navigator.userAgent || '');
  function dprNow() {
    try { return Math.round((window.devicePixelRatio || 1) * 100) / 100 || 1; } catch (e) { return 1; }
  }
  /* C1b (sdk 2.6), mirroring the open beacon: Android WebViews (FB/IG
     in-app above all) can report the boot viewport in PHYSICAL pixels
     (CSS x dpr), which C1 below cannot catch - the values are large, not
     small - and which classified most Android sessions as desktop. A
     PORTRAIT viewport wider than phone-scale CSS px on a Mobile UA is
     never a desktop: correct it via dpr when that lands back at phone
     scale, flag it either way. Both readers (ctx build and the C1
     remeasure) go through here so a late re-read cannot reintroduce the
     physical-px value. */
  function readViewport() {
    var w = window.innerWidth || 0, h = window.innerHeight || 0, flagged;
    var dpr = dprNow();
    if (MOB && h > w && w > 500) {
      flagged = true;
      if (dpr > 1.2 && dpr <= 5 && (w / dpr) <= 500) {
        w = Math.round(w / dpr); h = Math.round(h / dpr);
      }
    } else if (MOB && w > 950) {
      flagged = true;  /* wider than any phone in CSS px, even landscape */
    }
    return { w: w, h: h, flagged: flagged };
  }
  function deviceClass(w) {
    if (MOB) return 'mobile';  /* width is not trusted on a Mobile UA (C1b) */
    var x = w || window.innerWidth || (screen && screen.width) || 0;
    return x && x < 768 ? 'mobile' : x < 1024 ? 'tablet' : 'desktop';
  }
  function browserFamily(ua) {
    if (/instagram/i.test(ua)) return 'instagram';
    if (/fban|fbav|fb_iab/i.test(ua)) return 'facebook';
    if (/edg\//i.test(ua)) return 'edge';
    if (/chrome|crios/i.test(ua)) return 'chrome';
    if (/firefox|fxios/i.test(ua)) return 'firefox';
    if (/safari/i.test(ua)) return 'safari';
    return 'other';
  }
  function osFamily(ua) {
    if (/iphone|ipad|ipod|ios/i.test(ua)) return 'ios';
    if (/android/i.test(ua)) return 'android';
    if (/mac os/i.test(ua)) return 'macos';
    if (/windows/i.test(ua)) return 'windows';
    return 'other';
  }
  function refDomain() {
    try {
      if (!document.referrer) return null;
      var h = new URL(document.referrer).hostname;
      return h === location.hostname ? null : h;
    } catch (e) { return null; }
  }
  function abParts() {
    var raw = getCookie('_fm_ab');
    if (!raw) return [null, null];
    var i = raw.lastIndexOf('-');
    if (i < 1) return [raw, null];
    return [raw.slice(0, i), raw.slice(i + 1)];
  }

  var ua = navigator.userAgent || '';
  var ab = abParts();
  var isExcluded = !!(cfg.testId && getCookie('_fm_excl') === cfg.testId);
  var vp0 = readViewport();
  var ctx = {
    anon_id: anonId(),
    session_id: sessionId(true),
    page_path: capped(location.pathname, 512),
    page_type: cfg.pageType || null,
    template: cfg.template || null,
    theme_id: cfg.themeId || null,
    ab_test: ab[0],
    ab_bucket: ab[1],
    device_class: deviceClass(vp0.w),
    browser: browserFamily(ua),
    os: osFamily(ua),
    viewport_w: vp0.w || null,
    viewport_h: vp0.h || null,
    viewport_flagged: vp0.flagged,
    referrer_domain: refDomain(),
    utm_source: capped(qs.get('utm_source'), 128), utm_medium: capped(qs.get('utm_medium'), 128),
    utm_campaign: capped(qs.get('utm_campaign'), 256), utm_content: capped(qs.get('utm_content'), 256),
    utm_term: capped(qs.get('utm_term'), 256),
    fbclid: capped(qs.get('fbclid'), 512), gclid: capped(qs.get('gclid'), 512),
    gbraid: capped(qs.get('gbraid'), 512), wbraid: capped(qs.get('wbraid'), 512),
    ttclid: capped(qs.get('ttclid'), 512),
    webdriver: navigator.webdriver === true || undefined,
    internal: isInternal || undefined,
    sdk_version: cfg.sdk || '2.0'
  };

  /* C1: heights of 4, 50, 76 and 135 px turned up in real mobile rows. They
     come from reading the viewport before layout settles, and because the
     envelope is per-pageview one bad reading poisons every event in it.
     Re-measure, take the better value, and if it is still impossible say so
     on the record rather than suppressing real interactions. ctx is shipped
     by reference at flush time, so a repair reaches every batch after it. */
  var VP_MIN = 200;
  function implausibleVP() {
    return !(ctx.viewport_w >= VP_MIN) || !(ctx.viewport_h >= VP_MIN);
  }
  function remeasureViewport(final) {
    try {
      var vp = readViewport();
      if (vp.w >= VP_MIN && vp.h >= VP_MIN) {
        ctx.viewport_w = vp.w; ctx.viewport_h = vp.h;
        /* a C1b-corrected re-read stays flagged: the raw measurement was
           still untrustworthy even though the small-value repair passed */
        ctx.viewport_flagged = vp.flagged;
        ctx.device_class = deviceClass(vp.w);
        return;
      }
      if (final) {
        /* screen dimensions are a coarse but sane last resort */
        if (screen && screen.width >= VP_MIN && screen.height >= VP_MIN) {
          ctx.viewport_w = vp.w >= VP_MIN ? vp.w : screen.width;
          ctx.viewport_h = vp.h >= VP_MIN ? vp.h : screen.height;
        }
        ctx.viewport_flagged = true;
      }
    } catch (e) {}
  }
  if (implausibleVP()) {
    ctx.viewport_flagged = true;
    try { requestAnimationFrame(function () { remeasureViewport(false); }); } catch (e) {}
    setTimeout(function () { if (implausibleVP()) remeasureViewport(true); }, 600);
  }

  /* ---------- queue + transport ---------- */

  var queue = [], seq = 0;

  /* pv is stamped here, centrally. It is the per-boot pageview key, and a key
     present on the engagement events but missing from add_to_cart or the
     fm:event bridge is worse than no key at all: a JOIN USING (pv) returns
     zero rows and no error. Call sites do not repeat it.
     page_open is the one type that can never carry pv, and not by oversight:
     it is sent by the inline head beacon in snippets/site-events-open.liquid,
     which runs before this file loads and so cannot see PAGEVIEW_ID. The two
     transports are joinable only on session_id / anon_id. Do not try to fix
     this by stamping pv in the beacon; the ids would be two different uuids. */
  function emit(type, props) {
    props = props || {};
    if (props.pv === undefined) props.pv = PAGEVIEW_ID;
    if (AUDIT && props.audit_code === undefined) props.audit_code = AUDIT;
    queue.push({ id: uuid(), t: Date.now(), type: type, seq: ++seq, props: props });
    sessionId(true);
    /* size trigger only; the CLOCK trigger is the periodic backstop armed in
       boot() (sdk 2.2), which replaced a one-shot 10s timer armed here. */
    if (queue.length >= 20) flush();
  }
  function flush() {
    while (queue.length) {
      /* 40 was the ingest's own per-batch cap; it now accepts 120, and
         element_view makes long tails routine */
      var batch = queue.splice(0, 40);
      var body = JSON.stringify({ v: 1, ctx: ctx, events: batch });
      try {
        var ok = navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }));
        if (!ok && window.fetch) fetch(ENDPOINT, { method: 'POST', body: body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(function () {});
      } catch (e) {}
    }
  }

  /* audit_state marks the exact transition moments (sdk 2.3) so the
     dashboard's countdown and expiry banner ride an event instead of an
     inference. audit_code is stamped EXPLICITLY here, not left to emit():
     the off event fires when AUDIT is already (or about to be) null, and an
     off event that does not carry the code of the stream it closes cannot
     be matched to that stream. */
  function emitAuditState(p) {
    emit('audit_state', { code: p.code, state: p.state, reason: p.reason, audit_code: p.code });
  }

  /* ---------- consent gate ---------- */

  function consentOk() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return true;
      if (typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
      if (typeof cp.currentVisitorConsent === 'function') {
        var c = cp.currentVisitorConsent();
        return !c || c.analytics !== 'no';
      }
      return true;
    } catch (e) { return true; }
  }

  /* ---------- behaviors ---------- */

  var pageStart = Date.now(), hiddenAt = 0, hiddenTotal = 0, exitLatch = false;
  var maxScroll = 0, maxScrollPx = 0, milestonesHit = {};
  var secState = {}; /* key -> {viewed, since, dwell, timer} */
  var elState = {};  /* track -> {viewed, since, visible, peak, dwell, timer, el} */
  var rails = {};    /* key -> {max, changes, autoChanges, count, last, ids, ms, lastAt} */

  function onScroll() {
    var doc = document.documentElement;
    var y = scrollY();
    if (y > maxScrollPx) maxScrollPx = y;
    var range = doc.scrollHeight - window.innerHeight;
    /* NOTE for anyone reading max_scroll_pct: the denominator is
       scrollHeight - innerHeight, so a page shorter than the viewport scores
       100 (range <= 0) and a session that never scrolls stays at the initial
       0, because this runs once at boot and the guard below keeps it there. */
    var pct = range > 0 ? Math.min(100, Math.round((window.scrollY / range) * 100)) : 100;
    if (pct <= maxScroll) return;
    maxScroll = pct;
    [25, 50, 75, 100].forEach(function (m) {
      if (pct >= m && !milestonesHit[m]) { milestonesHit[m] = 1; emit('scroll_depth', { pct: m }); }
    });
  }

  /* ---------- B3: scroll trace ---------- */

  var TRACE_SAMPLE_MS = 300, TRACE_CHUNK = 40, TRACE_MAX = 1200;
  var traceBuf = [], traceCount = 0, traceLast = 0;

  function sampleScroll() {
    if (MINIMAL || traceCount >= TRACE_MAX) return;
    var now = Date.now();
    if (now - traceLast < TRACE_SAMPLE_MS) return;
    traceLast = now;
    traceCount++;
    traceBuf.push([now - pageStart, scrollY(), vpH()]);
    if (traceBuf.length >= TRACE_CHUNK) flushTrace();
  }
  /* Incremental by design: one payload at exit is the likeliest way to lose
     the only signal that cannot be rebuilt later. */
  function flushTrace() {
    if (!traceBuf.length) return;
    var chunk = traceBuf;
    traceBuf = [];
    emit('scroll_trace', { doc_h: docH(), trace: chunk });
  }

  /* ---------- section + element visibility ---------- */

  function sectionKey(el) {
    var id = el.id || '';
    if (id.indexOf('shopify-section-') === 0) id = id.slice(16);
    /* JSON templates prefix section ids: template--<id>__hero -> hero.
       Section GROUPS (header/footer) prefix theirs as sections--<id>__header;
       stripped since sdk 2.1 so the header aggregates as 'header' everywhere
       instead of a per-theme raw id. */
    id = id.replace(/^template--\d+__/, '');
    id = id.replace(/^sections--\d+__/, '');
    return el.getAttribute('data-track-section') || id || null;
  }

  /* Shared rule for "seen", used by sections and elements alike so the two
     metrics mean the same thing: half of it visible, OR (tall blocks on
     phones, which can never reach 50%) covering 60% of the viewport, held for
     a full second. */
  function qualifies(en) {
    /* Zero-area targets are invisible, but the spec reports them with
       intersectionRatio 1 while intersecting, so without this guard a
       display:none section (its wrapper collapsed to 0px) records a
       section_view as it scrolls past - the v4 replica's desktop-only story
       section did exactly that on phones (sdk 2.1). */
    var r = en.boundingClientRect;
    if (!r || r.height < 2 || r.width < 2) return false;
    var covered = en.intersectionRect && en.intersectionRect.height >= window.innerHeight * 0.6;
    return en.isIntersecting && (en.intersectionRatio >= 0.5 || covered);
  }
  var VIEW_MS = 1000;
  var THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.75, 1];

  function watchSections() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var key = sectionKey(en.target);
        if (!key) return;
        var st = secState[key] || (secState[key] = { dwell: 0 });
        if (qualifies(en)) {
          st.visible = 1;
          if (!st.since) st.since = Date.now();
          /* kept on the state so pauseVisibility can re-arm it. Backgrounding
             clears the timer (a view must not fire for something nobody was
             looking at), and IntersectionObserver fires on CHANGE - a tab
             coming back to the foreground is not one - so nothing else would
             ever replace it. */
          st.arm = function () {
            st.timer = setTimeout(function () {
              st.timer = null; st.viewed = 1;
              emit('section_view', { section: key });
            }, VIEW_MS);
          };
          if (!st.viewed && !st.timer) st.arm();
        } else {
          st.visible = 0;
          if (st.timer) { clearTimeout(st.timer); st.timer = null; }
          if (st.since) { st.dwell += Date.now() - st.since; st.since = 0; }
        }
      });
    }, { threshold: THRESHOLDS });
    var els = document.querySelectorAll('.shopify-section, [data-track-section]');
    for (var i = 0; i < els.length; i++) io.observe(els[i]);
  }

  /* B1. Once per element per pageview, fired on the crossing rather than
     held to exit, carrying geometry measured at that moment. ~20 rows a
     session, which is the ceiling the analysis side budgeted for. */
  var ELEMENT_LIMIT = 80;
  var elDropped = 0;   /* module scope: read by watchElements and sendExit */
  /* IntersectionObserver reads geometry, never paint. The cart drawer's scrim
     is a full-viewport [data-lvc-close] div that lander-cart.css hides with
     opacity:0, so it qualified as "seen" on every pageview - and because add()
     walks document order it also claimed the clean key drawer:close and demoted
     the genuinely clickable .lvc__x button to drawer:close#2, while clicks on
     either resolved to plain drawer:close. Views and clicks on that one name
     were describing two disjoint elements.
     Excluded by structure rather than by naming the single node: .lvc is the
     drawer SHELL, so anything inside it that is not inside .lvc__panel is a
     full-bleed invisible layer by construction - this one and the next one.
     The panel's own contents stay observed: they are hidden by an off-screen
     transform, which IntersectionObserver DOES honour, so they qualify only
     once the drawer actually opens.
     Deliberately no computed-style test: .lv1-reveal / .lv2-reveal start at
     opacity 0 and a style filter would drop every fade-in block for good. */
  function viewTarget(el) {
    if (!el || !el.closest) return true;
    return !(el.closest('.lvc') && !el.closest('.lvc__panel'));
  }
  function watchElements() {
    if (!('IntersectionObserver' in window)) return;
    var seen = [], targets = [];
    function add(el, name) {
      /* data-track-view="off" suppresses the VIEW row while leaving the
         node's data-track click id untouched. Authored opt-out, deliberately
         separate from viewTarget()'s structural exclusion: that one answers
         "is this node a legitimate thing to observe at all" and is the SDK's
         call, this one is the page author's declaration, and merging them
         would destroy the ability to tell a workaround from an intent.

         The comparison is EXACT and CASE-SENSITIVE by design. "Off", "OFF",
         "false", "no", "" and a bare attribute all leave the element
         OBSERVED. The failure direction is one extra row, which is free to
         ignore at read time; a loose comparison fails toward a MISSING row on
         a template that freezes the moment it takes traffic. "on" is reserved
         as an inert word for a future allowlist mode. Do not "clean this up"
         into a truthiness or case-insensitive test.

         Order is deliberate: !el guards the getAttribute call, and the single
         attribute read sits ahead of viewTarget()'s two closest() walks. */
      if (!el || !name ||
          (el.getAttribute && el.getAttribute('data-track-view') === 'off') ||
          !viewTarget(el) || seen.indexOf(el) !== -1) return;
      /* above the limit test on purpose: a node reachable from BOTH sources (a
         [data-track] element that also matches a CLICK_MAP selector) would
         otherwise be counted twice by elDropped. In both orderings a node over
         the limit returns without being added to targets, and seen is read for
         nothing but this dedupe, so v4b is unaffected. */
      seen.push(el);
      if (targets.length >= ELEMENT_LIMIT) { elDropped++; return; }
      var key = name, n = 2;
      while (elState[key]) key = name + '#' + n++;
      elState[key] = { dwell: 0, peak: 0, el: el };
      targets.push([el, key]);
    }
    var tagged = document.querySelectorAll('[data-track]');
    for (var i = 0; i < tagged.length; i++) add(tagged[i], tagged[i].getAttribute('data-track'));
    for (var m = 0; m < CLICK_MAP.length; m++) {
      var found = document.querySelectorAll(CLICK_MAP[m][0]);
      for (var j = 0; j < found.length; j++) add(found[j], CLICK_MAP[m][1]);
    }
    /* Truncation at ELEMENT_LIMIT used to be silent, and a template freezes
       the moment it takes traffic, so an overflowing page is unrecoverable for
       the life of the test. All three channels are free when nothing dropped.
       __fmWatch is what the pre-launch driver reads: grepping the HTML for
       data-track= counts TAGS, not observed targets, and from 2.7 those two
       diverge the moment anything opts out. */
    window.__fmWatch = { n: targets.length, limit: ELEMENT_LIMIT, dropped: elDropped };
    if (elDropped && window.console && console.warn) {
      console.warn('[fm] element_view: ' + elDropped + ' element(s) over the ' +
                   ELEMENT_LIMIT + ' limit are not observed');
    }
    if (!targets.length) return;
    var byEl = function (target) {
      for (var k = 0; k < targets.length; k++) if (targets[k][0] === target) return targets[k][1];
      return null;
    };
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var key = byEl(en.target);
        if (!key) return;
        var st = elState[key];
        var pct = Math.round((en.intersectionRatio || 0) * 100);
        if (pct > st.peak) st.peak = pct;
        if (qualifies(en)) {
          st.visible = 1;
          if (!st.since) st.since = Date.now();
          /* re-armable for the same reason as section_view above */
          st.arm = function () {
            st.timer = setTimeout(function () {
              st.timer = null;
              if (st.viewed) return;
              st.viewed = 1;
              emit('element_view', geo(st.el, {
                track: key.split('#')[0],
                instance: key.indexOf('#') === -1 ? undefined : key,
                visible_pct: st.peak,
                /* attention clock, not wall clock. The view timer is cleared at
                   hide and re-armed on resume, so this fires AFTER a background
                   and on the raw clock would report ten locked-phone minutes
                   against a page_exit.dwell_ms of two seconds. Same subtraction
                   dwell_ms makes, so the two stay comparable. */
                first_seen_ms: Date.now() - pageStart - hiddenTotal
              }));
            }, VIEW_MS);
          };
          if (!st.viewed && !st.timer) st.arm();
        } else {
          st.visible = 0;
          if (st.timer) { clearTimeout(st.timer); st.timer = null; }
          if (st.since) { st.dwell += Date.now() - st.since; st.since = 0; }
        }
      });
    }, { threshold: THRESHOLDS });
    for (var t = 0; t < targets.length; t++) io.observe(targets[t][0]);
  }

  /* A backgrounded tab fires no intersection callbacks, so an open timer
     would otherwise bank four minutes of "attention" from a phone left on a
     table. Close the books when hidden, reopen for whatever is still on
     screen when it comes back. */
  function pauseVisibility(paused) {
    [secState, elState].forEach(function (map) {
      for (var k in map) {
        var st = map[k];
        if (paused) {
          /* the dwell timer was already closed here; the VIEW timer was not,
             so a section one tick short of qualifying when the phone locked
             recorded a view for something that was never on screen again. */
          if (st.timer) { clearTimeout(st.timer); st.timer = null; }
          if (st.since) { st.dwell += Date.now() - st.since; st.since = 0; }
        } else if (st.visible) {
          st.since = Date.now();
          if (!st.viewed && !st.timer && st.arm) st.arm();
        }
      }
    });
    /* rails run their own clock (slide_ms), on the same rule */
    for (var r in rails) {
      if (paused) railPause(rails[r]);
      else if (rails[r].visible) railResume(rails[r]);
    }
  }

  /* ---------- E1: rails / carousels ---------- */

  /* any future swipeable rail opts in with data-track-rail - no JS change.
     data-carousel="<rail name>" additionally NAMES it (sdk 2.4): named rails
     emit a state_change per settled slide change so the replay can follow
     them, and the attribute is the replay's generic way to find the rail in
     the DOM snapshot. */
  var RAIL_SELECTOR = '[data-carousel], [data-track-rail], [data-fx-cards], .lv2-carousel, .lv1-cardrow';
  /* A rail's slides are its children - except ReelUp-style scrollers, which
     put ONE full-width sizer div inside the scroll element and absolutely
     position the slides in that. There the grandchildren are the slides. The
     width test keeps a single-slide rail with internal structure from being
     misread as many slides. */
  function railSlides(el) {
    var kids = el.children;
    if (kids.length === 1 && kids[0].children.length > 1 &&
        kids[0].offsetWidth > el.clientWidth + 8) return kids[0].children;
    return kids;
  }
  /* A slide's IDENTITY, not its position: if slide order differs between arms
     or moves mid-test, index 3 means two different things and the comparison
     breaks with no error anywhere. */
  function slideId(el, i) {
    if (!el) return String(i);
    var explicit = el.getAttribute && (el.getAttribute('data-slide-id') || el.getAttribute('data-track'));
    if (explicit) return explicit;
    var h = el.querySelector && el.querySelector('h1, h2, h3, h4, [data-slide-title]');
    var text = h && h.textContent ? h.textContent : '';
    text = text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
    return text || String(i);
  }
  /* slide_ms used to start its clock at rail construction, i.e. at page load,
     so every second before the rail was ever scrolled into view - plus every
     backgrounded minute - was banked against slide 1, and slide 1 looked like
     the runaway winner on every rail in the dataset. The clock now runs only
     while the rail is on screen under the SAME qualifies() rule sections and
     elements use, so the three metrics mean the same thing.
     lastAt === 0 means the clock is stopped, so both of these are no-ops when
     it already is - which is what keeps double-banking out of the exit path. */
  function railPause(st) {
    if (!st.lastAt) return;
    var cur = st.ids[st.last] || String(st.last);
    st.ms[cur] = (st.ms[cur] || 0) + (Date.now() - st.lastAt);
    st.lastAt = 0;
  }
  function railResume(st) {
    if (!st.lastAt) st.lastAt = Date.now();
  }
  function watchRails() {
    var byRail = [];
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var st = null;
          for (var b = 0; b < byRail.length; b++) if (byRail[b][0] === en.target) st = byRail[b][1];
          if (!st) return;
          if (qualifies(en)) { st.visible = 1; railResume(st); }
          else { st.visible = 0; railPause(st); }
        });
      }, { threshold: THRESHOLDS });
    }
    var nRails = 0;
    function addRail(el) {
      for (var b = 0; b < byRail.length; b++) if (byRail[b][0] === el) return;
      var i = nRails++;
      var sec = el.closest && el.closest('.shopify-section');
      var section = sec ? sectionKey(sec) : 'page';
      var key = section + ':' + i;
      var controlled = el.hasAttribute('data-fm-rail-controlled');
      var slides = railSlides(el);
      var ids = [];
      for (var c = 0; c < slides.length; c++) ids.push(slideId(slides[c], c));
      var st = rails[key] = {
        section: section, index: i, max: 0, changes: 0, autoChanges: 0, count: slides.length,
        last: 0, ids: ids, ms: {}, lastAt: 0, visible: 0
      };
      byRail.push([el, st]);
      /* no IntersectionObserver: fall back to the old always-on clock rather
         than shipping an empty slide_ms on those browsers */
      if (io) io.observe(el);
      else { st.visible = 1; railResume(st); }
      /* recomputed per read, not cached: ReelUp adds slides as it loads */
      function idxNow() {
        var s = railSlides(el);
        var w = s[0] ? s[0].offsetWidth : el.clientWidth;
        return Math.min(Math.max(0, s.length - 1), Math.round(el.scrollLeft / Math.max(1, w)));
      }
      if (controlled) {
        // The page owns selection for centered cards and non-scrolling desktop
        // lists. Do not infer their index from scrollLeft or count both paths.
        var startIndex = Number(el.getAttribute('data-fm-rail-index')) || 0;
        st.last = Math.min(Math.max(0, slides.length - 1), Math.max(0, startIndex));
        st.max = st.last;
        el.addEventListener('fm:rail-state', function (event) {
          var d = event.detail || {}, idx = d.index;
          if (typeof idx !== 'number' || !isFinite(idx) || idx % 1 || idx < 0 || idx >= railSlides(el).length || idx === st.last) return;
          railPause(st); st.changes++;
          if (d.auto === true) st.autoChanges++;
          st.last = idx; st.max = Math.max(st.max, idx);
          if (st.visible && !document.hidden) railResume(st);
          var name = el.getAttribute('data-carousel');
          if (name) emit('state_change', {
            track: 'carousel:' + name, control: 'carousel', carousel: name,
            index: idx, slide_id: slideId(railSlides(el)[idx], idx),
            auto: d.auto === true || undefined,
            media_time: typeof d.media_time === 'number' ? round(d.media_time * 1000) / 1000 : undefined
          });
        });
        return;
      }
      el.addEventListener('scroll', throttle(function () {
        var idx = idxNow();
        if (idx !== st.last) {
          railPause(st); /* bank the OUTGOING slide, before st.last moves */
          st.changes++;
          if (el.hasAttribute('data-fm-auto')) st.autoChanges++;
          st.last = idx;
          if (idx > st.max) st.max = idx;
          if (st.visible) railResume(st);
        }
      }, 300), { passive: true });
      /* sdk 2.4, replay fidelity: a NAMED rail also reports each SETTLED
         slide change as its own event - the cumulative summary above cannot
         be replayed and swipes produce no clicks. Settled means 300ms of
         scroll silence (trailing debounce, unlike the summary's leading
         throttle), so a fling across three slides is ONE event carrying the
         landing slide. Fires for programmatic scrolls too (the gallery's
         goTo(0) on swatch change), which is exactly what replay wants: the
         gallery visibly reset. SDK 2.9 adds auto:true when a page-owned timer
         moved the rail; user movement keeps the field absent. */
      var cname = el.getAttribute('data-carousel');
      if (cname) {
        var settleT = null, lastEmit = 0, sawAuto = false;
        el.addEventListener('scroll', function () {
          if (el.hasAttribute('data-fm-auto')) sawAuto = true;
          if (settleT) clearTimeout(settleT);
          settleT = setTimeout(function () {
            settleT = null;
            var s = railSlides(el);
            var idx = idxNow();
            /* Reset before the same-index return. Otherwise an automatic
               no-op can contaminate the next real swipe. */
            var auto = sawAuto;
            sawAuto = false;
            if (idx === lastEmit) return;
            lastEmit = idx;
            emit('state_change', {
              track: 'carousel:' + cname, control: 'carousel',
              carousel: cname, index: idx, slide_id: slideId(s[idx], idx),
              auto: auto || undefined
            });
          }, 300);
        }, { passive: true });
      }
    }
    var els = document.querySelectorAll(RAIL_SELECTOR);
    for (var i = 0; i < els.length; i++) addRail(els[i]);
    /* Rails that exist only after boot: ReelUp hydrates its scroller late and
       the page stamps data-carousel on it once it exists. Watch for both the
       node arriving and the attribute landing on an existing node; addRail
       dedupes, so over-calling is safe. */
    if ('MutationObserver' in window) {
      var scan = function (n) {
        if (!n || n.nodeType !== 1) return;
        if (n.matches && n.matches(RAIL_SELECTOR)) addRail(n);
        if (n.querySelectorAll) {
          var found = n.querySelectorAll(RAIL_SELECTOR);
          for (var f = 0; f < found.length; f++) addRail(found[f]);
        }
      };
      new MutationObserver(function (muts) {
        for (var m = 0; m < muts.length; m++) {
          if (muts[m].type === 'attributes') scan(muts[m].target);
          else for (var a = 0; a < muts[m].addedNodes.length; a++) scan(muts[m].addedNodes[a]);
        }
      }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-carousel'] });
    }
  }

  /* Optional layout history for pages whose controls or app widgets reflow.
     This does not replace scroll traces or fabricate element_view events. */
  function watchLayout() {
    if (!document.querySelector('[data-fm-layout]')) return;
    var timer = null, previous = '', nodes = [];
    function snapshot(reason) {
      timer = null;
      if (document.hidden) return;
      var sections = [];
      for (var i = 0; i < nodes.length && sections.length < 24; i++) {
        var node = nodes[i], key = sectionKey(node), r = node.getBoundingClientRect();
        if (!key || r.width < 2 || r.height < 2 || isFixed(node)) continue;
        sections.push({id: key.slice(0, 80), top: round(r.top + scrollY()), h: round(r.height)});
      }
      var shape = {doc_h: docH(), vw: round(window.innerWidth), vh: vpH(), sections: sections};
      var signature = JSON.stringify(shape);
      if (signature === previous) return;
      // Bound trace chunks at a layout change; snapshots retain both shapes.
      flushTrace(); previous = signature;
      emit('state_change', Object.assign({control:'layout', track:'page:layout', reason:reason, scroll_y:scrollY()}, shape));
      sampleScroll();
    }
    function schedule() { clearTimeout(timer); timer = setTimeout(function () { snapshot('resize'); }, 200); }
    nodes = Array.prototype.slice.call(document.querySelectorAll('.shopify-section, [data-track-section]'));
    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(schedule);
      nodes.forEach(function (node) { observer.observe(node); });
      observer.observe(document.documentElement);
    }
    window.addEventListener('resize', schedule, {passive:true});
    if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule, {passive:true});
    document.addEventListener('visibilitychange', function () { if (!document.hidden) schedule(); });
    snapshot('initial');
  }

  /* ---------- clicks ---------- */

  var lastTouch = 0;
  var CLICK_MAP = [
    ['[data-lvc-checkout]', 'drawer:checkout'],
    ['[data-lvc-bubble]', 'drawer:bubble'],
    ['[data-lvc-upsell-add]', 'drawer:upsell-add'],
    ['[data-lvc-inc]', 'drawer:qty-inc'],
    ['[data-lvc-dec]', 'drawer:qty-dec'],
    ['[data-lv1-add-bundle]', 'offer:add-to-cart'],
    ['.lv1-bar__cta', 'bar:jump-to-offer'],
    ['.lv2-bar__cta', 'bar:jump-to-offer'],
    /* Added 2026-08-20: the untracked-click audit showed these were the biggest
       unnamed buckets on both arms. Named here rather than via data-track so the
       tested templates stay byte-identical (AGENTS.md: tested pages are immutable).
       [data-lvc-to-offer] must stay ahead of [data-lvc-close]: the empty-cart
       button carries both attributes and the first match in this list wins.
       .lvc__overlay goes ahead of it for the same reason and for a second one:
       the scrim also carries [data-lvc-close], so a backdrop tap used to report
       as drawer:close - but only .lvc__x is observable for element_view (the
       scrim is excluded by viewTarget), so clicks on that name outnumbered its
       views several times over on mobile, where the backdrop is the dominant
       close gesture. Separate names, one element each, and backdrop dismiss is
       the more interesting of the two anyway.
       ENTRY SHAPE: [selector, name] or [selector, name, interactive]. The
       optional third element is a BOOLEAN assertion about interactivity, for
       nodes the INTERACTIVE_SEL probe cannot read. Only a human looking at the
       page can make it, so it is per entry and never inferred: the scrim is a
       bare div WITH a real handler (lander-cart.js, close('backdrop')), while
       .lv1-drink is a bare div with no handler anywhere - which is precisely
       why it is in this list, and why it must keep reporting interactive:false.
       Omit the third element and the entry asserts nothing; the probe decides,
       as before. */
    ['[data-lvc-to-offer]', 'drawer:empty-to-offer'],
    ['.lvc__overlay', 'drawer:dismiss', true],
    ['[data-lvc-close]', 'drawer:close'],
    ['.lv1-hero__copy .lv1-btn', 'hero:cta'],
    ['.lv1-hero__rating', 'hero:rating-jump'],
    ['.lv1-drinks__cta-wrap .lv1-btn', 'drinks:cta'],
    ['.lv1-drink', 'drinks:card', false],
    /* Added 2026-08-21 (sdk 2.1, slv4): the THEME's cart drawer, which the v4
       replica opens after an add and which renders inside the header section
       group - its taps were landing as unnamed header-section clicks. Names
       shared with the lander drawer entries above on purpose: same function,
       same name, analysis splits by page (R2). Below the lander entries
       because the order-guard test pins drawer:dismiss ahead of any
       drawer:close, and no #CartDrawer selector can overlap a lander node
       anyway. The checkout name must stay in step with the checkout_start
       emit in assets/v4-page.js. */
    ['#CartDrawer [name="checkout"]', 'drawer:checkout'],
    ['#CartDrawer .js-drawer-close', 'drawer:close'],
    ['#CartDrawer .js-qty__adjust--plus', 'drawer:qty-inc'],
    ['#CartDrawer .js-qty__adjust--minus', 'drawer:qty-dec'],
    /* Dead-space taps on the drawer footer used to record with the whole
       cart-summary string as their label; one clean name, pinned
       non-interactive like .lv1-drink (no handler on the footer itself -
       the buttons inside carry their own names and win as innermost). */
    ['#CartDrawer .drawer__footer', 'drawer:summary', false],
    /* ReelUp video taps (the videos strip and the two FAQ embeds) and the
       Okendo review-carousel arrows - the biggest unnamed buckets in the
       v4 QA data. Third-party markup, so named here rather than tagged. */
    ['.reelUp_playlist_js_widget video', 'videos:play'],
    ['.oke-prevNextButton--prev', 'reviews:prev'],
    ['.oke-prevNextButton--next', 'reviews:next'],
    ['.lv1-bar__logo', 'bar:logo']
  ];
  /* Returns {name, node} - never a bare string. The node is the element that
     PRODUCED the name, which is not the element that was tapped: a tap on the
     <b> inside a tier card has to report the CARD's box, tag and label, or
     props.track and props.el end up describing different elements and the
     click heat map measures the wrong rectangle.
     The CLICK_MAP exit adds a third key, `interactive`, carrying that entry's
     assertion (see the list above) - true, false, or undefined for "no claim".
     The other three exits make no claim: swatches are <button>, learn-more is
     an <a>, and a [data-track] node can be anything, so INTERACTIVE_SEL is the
     right answer for all of them. */
  function nameFor(el) {
    if (!el.closest) return null;
    /* Swatches and in-card links resolve BEFORE the generic [data-track] lookup.
       On arm B the colour swatches and the "Learn more" link sit INSIDE
       .lv1-osel-card, which itself carries data-track="offer:tier-N". The old
       order let closest('[data-track]') match the card first, so a colour change
       or a link tap was reported as a tier selection. The page does the
       opposite: lander-offer-select.js bails out of select(card) for exactly
       these two cases, so a swatch or link click never changes the chosen tier.
       The tracker now mirrors the page. Which card the click happened in is
       still recorded independently as props.extra.opt. */
    var sw = el.closest('.lv1-swatch, .lv2-swatch');
    if (sw) return { name: 'swatch:' + (sw.getAttribute('data-color-name') || 'unknown').toLowerCase().replace(/\s+/g, '-'), node: sw };
    var card = el.closest('.lv1-osel-card[data-opt]');
    if (card) {
      var lnk = el.closest('a');
      if (lnk && card.contains(lnk)) return { name: 'offer:learn-more', node: lnk };
    }
    var t = el.closest('[data-track]');
    if (t) return { name: t.getAttribute('data-track'), node: t };
    for (var i = 0; i < CLICK_MAP.length; i++) {
      var mapped = el.closest(CLICK_MAP[i][0]);
      if (mapped) return { name: CLICK_MAP[i][1], node: mapped, interactive: CLICK_MAP[i][2] };
    }
    return null;
  }
  /* A3's probe, and the primary answer for interactive: a named node is NOT
     interactive by construction - half of CLICK_MAP exists because people tap
     things that do nothing, and treating "we gave it a name" as "it has a
     handler" collapses the column onto track IS NOT NULL, which deletes the
     dead-click metric outright.
     [role="radio"] is the omission that mattered: arm B's offer cards are
     <div role="radio" data-track="offer:tier-N"> with the handler in
     lander-offer-select.js, so every real tier selection was being filed as a
     dead click. The rest are the same shape of handler-on-a-div.
     tabindex="-1" is excluded deliberately: it means "focusable by script, not
     by the user", and the theme uses it on whole-lightbox wrappers
     (photoswipe-template, predictive-search), so counting it would report every
     click inside a lightbox - the canonical dead tap included - as interactive
     the moment this SDK is widened past the landers. */
  var INTERACTIVE_SEL = 'a, button, [role="button"], [role="radio"], [role="option"], [role="tab"], [role="switch"], input, label, summary, [tabindex]:not([tabindex="-1"])';
  function onClick(e) {
    try {
      var el = e.target;
      if (!el || el.nodeType !== 1) return;
      var interactive = el.closest ? el.closest(INTERACTIVE_SEL) : null;
      var hit = nameFor(el);
      var name = hit ? hit.name : null;
      /* '.shopify-section, [data-track-section]', matching the VIEW path at
         the querySelectorAll above. They disagreed until 2026-08-26: views
         honoured an explicit data-track-section and clicks did not, so a
         surface that opts into its own section name produced section_view rows
         under that name and zero click rows, while its clicks were absorbed
         by whatever section it happens to be RENDERED from. On slv5 that is
         the Krak'in modal, rendered from inside the cart drawer section: every
         krakin-modal:* click was filed as `drawer`, so the gift modal's
         clicks-per-view read 0/N and drawer click volume was inflated by
         traffic that never touched the cart. Both silent.
         Provably inert elsewhere: `data-track-section` appears on exactly one
         element in the whole theme. */
      var sec = el.closest ? el.closest('.shopify-section, [data-track-section]') : null;
      var doc = document.documentElement;
      var extra = null;
      /* The named node wins as the measurement source: it is the element
         props.track is about. Falling back to the tapped child made el.w/h,
         tag and label describe an inner <b> or <img> instead of the card. */
      var src = (hit && hit.node) || interactive || el;
      var opt = src.closest && src.closest('[data-opt]');
      if (opt) extra = { opt: opt.getAttribute('data-opt') };
      var slotEl = src.closest && src.closest('[data-slot]');
      if (slotEl) { extra = extra || {}; extra.slot = slotEl.getAttribute('data-slot'); }
      var bundleAttr = src.getAttribute && (src.getAttribute('data-lv1-add-bundle') || null);
      if (bundleAttr) { extra = extra || {}; extra.bundle = bundleAttr; }
      emit('click', geo(src, {
        track: name,
        section: sec ? sectionKey(sec) : null,
        tag: src.tagName.toLowerCase(),
        /* what was actually HIT, kept alongside src's tag. tag/label describe
           the NAMED node and so are constant per track by construction, which
           on its own disables AGENTS.md R10's cross-tag canary: one track
           appearing against several tags is the signature of a container id
           swallowing its children. target_tag is the column that check reads.
           It is the nearest INTERACTIVE_SEL ancestor of the tapped node, else
           the tapped node itself - not the raw tap target, because every
           button holding an <svg> and a <span> would otherwise report three
           tags and the canary would be pure noise. So a data-track wrapper
           holding a link (the AGENTS.md:255 anti-pattern) shows a and div
           under one track; a wrapper that is itself interactive, like arm B's
           <div role="radio"> tier card, still reports one tag. */
        target_tag: (interactive || el).tagName.toLowerCase(),
        label: (src.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60) || null,
        /* A3: dead clicks (a tap on an image expecting a lightbox, on a price
           expecting a tier change) are a design finding, and inferring them
           from the tag is guesswork the click handler can answer directly.
           The probe decides unless the CLICK_MAP entry that named this node
           asserts otherwise; a false assertion cannot override a probe hit,
           because an inert div wrapped in an <a> really is interactive. */
        interactive: !!(interactive || (hit && hit.interactive)),
        /* x_pct/y_pct stay the ACTUAL touch point, not src's centre: where in
           the page they tapped is the question, src answers what they hit. */
        x_pct: Math.round((e.pageX / Math.max(1, doc.scrollWidth)) * 1000) / 10,
        y_pct: Math.round((e.pageY / Math.max(1, doc.scrollHeight)) * 1000) / 10,
        /* sdk 2.4: the moment's own viewport height and doc-space tap y.
           ctx.viewport_h is boot-time; on iOS the URL-bar collapse moves the
           live height >100px within one pageview, so replay needs both
           stamped here. click_y == pageY == scroll_y + clientY; vh shares
           vpH() with the scroll-trace points so the two never disagree. */
        vh: vpH(),
        click_y: round(e.pageY),
        pointer: (e.pointerType === 'touch' || Date.now() - lastTouch < 700) ? 'touch' : 'mouse',
        extra: extra || undefined
      }));
    } catch (err) {}
  }

  /* ---------- exit ---------- */

  function sendExit() {
    if (exitLatch) return;
    exitLatch = true;
    var now = Date.now();
    var dwellMap = {}, elMap = {}, k, st;
    for (k in secState) {
      st = secState[k];
      if (st.since) { st.dwell += now - st.since; st.since = 0; }
      if (st.dwell > 500) dwellMap[k] = st.dwell;
    }
    /* B1's enrichment half: which elements were seen is already on the record
       as its own event, so losing this map costs only the durations. */
    for (k in elState) {
      st = elState[k];
      if (st.since) { st.dwell += now - st.since; st.since = 0; }
      if (st.dwell > 500) elMap[k] = st.dwell;
    }
    for (k in rails) {
      st = rails[k];
      /* close the books on whatever slide is still on the clock. A no-op on
         the visibilitychange path, where pauseVisibility(true) already ran. */
      railPause(st);
      if (st.changes > 0 && !st.reported) {
        st.reported = true;
        emit('carousel', {
          /* the latch reopens on foreground, so this can repeat with
             cumulative totals. NOT one row per pv the way page_exit is: this is
             one row per RAIL per hide, and both arms render two rails, so
             dedupe on (pv, carousel, rail_index), max seq. Partitioning by pv
             alone silently deletes a whole rail from the dataset. */
          carousel: st.section, rail_index: st.index,
          max_index: st.max, count: st.count, changes: st.changes,
          changes_auto: st.autoChanges || undefined,
          slides: st.count,
          seen: st.ids.slice(0, st.max + 1),
          slide_ms: st.ms
        });
      }
    }
    flushTrace();
    emit('page_exit', {
      /* one row per hide, each carrying totals cumulative since boot. pv (the
         per-boot key, stamped in emit) is what makes "last exit wins" a
         one-line dedupe instead of a guess about ordering. */
      dwell_ms: now - pageStart - hiddenTotal - (hiddenAt ? now - hiddenAt : 0),
      max_scroll_pct: maxScroll,
      /* B2: raw pixels are unambiguous; a percentage needs its denominator
         documented, and the denominator here is not the obvious one. */
      max_scroll_px: maxScrollPx,
      doc_h: docH(),
      sections: dwellMap,
      elements: elMap,
      /* undefined is stripped by JSON.stringify, so a healthy page carries
         nothing on the wire. No new event type, no new column. page_view
         cannot carry it: watchElements() runs long after page_view flushes. */
      els_dropped: elDropped || undefined
    });
    flush();
  }

  /* ---------- boot ---------- */

  function boot() {
    var entry = false;
    try {
      entry = !sessionStorage.getItem('_fm_pv');
      sessionStorage.setItem('_fm_pv', '1');
    } catch (e) {}
    emit('page_view', { entry: entry, vis: document.visibilityState || undefined, doc_h: docH(), dpr: dprNow(), excluded: isExcluded || undefined });
    /* transitions the parse block banked (activation, cleared, boot-time
       expiry) ride the first batch, right behind the pageview */
    for (var ap = 0; ap < auditPending.length; ap++) emitAuditState(auditPending[ap]);
    flush(); /* ship the pageview immediately: in-app browsers can kill the
                page without ever firing visibilitychange/pagehide */

    /* sdk 2.2 cadence: a periodic backstop instead of the old one-shot 10s
       timer armed at first queue entry. 7s for customers - in-app browsers
       can die without ever firing pagehide, and the worst-case tail an event
       can lose should be seconds, not the whole gap to the next size-trigger.
       1s in audit mode, and there the TRACE buffer flushes on the clock too:
       the Event Tester replays the session while it happens, so partial
       chunks beat complete ones. Customers keep the chunk-size trace flush
       (40 samples) plus the exit flush in sendExit - a 7s trace backstop
       would triple the site_scroll_traces row count for zero replay value.
       Ticks with nothing buffered are free; hidden tabs (browsers throttle
       the interval) have already flushed via sendExit.
       sdk 2.3: the tick is also where the guardrail TTL is enforced - every
       fast-flush decision re-checks the window age, and at expiry the mode
       reverts IN PLACE (no reload): announce while the code still stamps,
       delete the keys, stop echoing, drop to the 7s clock. */
    var tickTimer = null, tickMs = 0;
    function tick() {
      if (AUDIT && Date.now() - AUDIT_AT >= AUDIT_TTL) {
        emitAuditState({ code: AUDIT, state: 'off', reason: 'expired' });
        try {
          sessionStorage.removeItem('_fm_audit');
          sessionStorage.removeItem('_fm_audit_at');
        } catch (e) {}
        AUDIT = null; AUDIT_AT = 0;
        schedule();
      }
      if (AUDIT) flushTrace();
      flush();
    }
    function schedule() {
      var want = AUDIT ? 1000 : 7000;
      if (tickTimer && want === tickMs) return;
      if (tickTimer) clearInterval(tickTimer);
      tickMs = want;
      tickTimer = setInterval(tick, want);
    }
    schedule();

    window.addEventListener('scroll', throttle(onScroll, 400), { passive: true });
    if (!MINIMAL) window.addEventListener('scroll', sampleScroll, { passive: true });
    window.addEventListener('touchstart', function () { lastTouch = Date.now(); }, { passive: true, capture: true });
    if (!MINIMAL) document.addEventListener('click', onClick, true);

    /* D1: a cart event that cannot say WHICH tier was chosen cannot answer
       the question the tier test exists to ask. The page supplies the line
       identity; everything here is passthrough. */
    document.addEventListener('fm:cart', function (e) {
      var d = (e && e.detail) || {};
      emit('add_to_cart', {
        added: d.added,
        /* EasyGift's attachments, counted separately: `added` is paid units
           only on both sides (lander-cart.js), so the gift units the app landed
           inside this cycle are their own field or they are lost.
           Named `gifts_in_cycle`, not `gifts_attached`, because the name has to
           be race-honest. It samples only the gift units present when the add
           cycle's settle window closes, and EasyGift usually lands after that,
           so SUM() of it under-counts by a latency-dependent fraction. The
           authoritative per-order gift count is the free_krakin_gift SKU on the
           order. This field answers one narrower question: did gifts land
           inside this add cycle. */
        gifts_in_cycle: d.gifts_in_cycle,
        item_count: d.item_count, value: d.value,
        items: d.items, added_items: d.added_items,
        tier: d.tier, swatches: d.swatches,
        cart_value: d.cart_value != null ? d.cart_value : d.value,
        cart_items: d.cart_items != null ? d.cart_items : d.item_count,
        track: d.track
      });
      flush(); /* buyers often leave for checkout right after; don't lose the tail */
    });

    /* Generic page -> SDK bridge (H1, H2, D2). Page code that knows something
       the tracker cannot see - cart contents, why a drawer closed, which line
       a stepper acted on - emits it here without importing anything. */
    document.addEventListener('fm:event', function (e) {
      var d = (e && e.detail) || {};
      if (!d || !d.type) return;
      emit(String(d.type).slice(0, 32), d.props || {});
      if (d.flush) flush();
    });

    /* Bridge-ready signal (SDK 2.5), for producers that parse before this
       deferred file runs - today the pixel-debug shim, which captures fbq
       calls from <head> and must not dispatch fm:event into the void. Flag
       AND event: the event reaches listeners registered earlier (the normal
       case), the flag covers a producer evaluated after boot. Deliberately
       placed after the fm:event listener above - the signal means "the
       bridge is live", nothing less. */
    window.__fmSdkUp = true;
    try { document.dispatchEvent(new CustomEvent('fm:sdk-ready')); } catch (err) {}

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        pauseVisibility(true);
        sendExit();
      } else {
        if (hiddenAt) { hiddenTotal += Date.now() - hiddenAt; hiddenAt = 0; }
        pauseVisibility(false);
        exitLatch = false;
        /* the exit latch reopens, so the carousel's must too. Without this a
           rail first swiped AFTER the visitor came back from a background -
           the common case on a phone - was never reported at all, because the
           latch had already been set by an exit that had nothing to report. */
        for (var rk in rails) rails[rk].reported = false;
      }
    });
    window.addEventListener('pagehide', sendExit);

    /* Nick's scope call for slv2 arm C (2026-08-20): the control page needs
       conversion rate and AOV comparability only. Section, element and click
       tracking on a page nobody instrumented would produce PageFly-generated
       names that mean nothing and cannot be compared with the landers anyway. */
    if (!MINIMAL) {
      watchSections();
      watchElements();
      watchRails();
      watchLayout();
    }
    onScroll();
  }

  if (consentOk()) boot();
  else document.addEventListener('visitorConsentCollected', function onConsent() {
    if (consentOk()) { document.removeEventListener('visitorConsentCollected', onConsent); boot(); }
  });
})();
